import Api from 'api';
import { ActionCreator, Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';
import {
  IRetailerSetSelectedProject,
  IRetailerSetSelectedProcessor,
  IRetailerSetSelectedFarmer,
  IRetailerSetKpisInfo,
  IRetailerKpiPendingInfo,
  IRetailerSetSelectedSystemType,
  RetailerKpiValueModel,
  ProjectInformationModel,
  IRetailerKpiSetProjectsInfo,
  IRetailerSetDrillDownProject,
  IRetailerSetDrillDownProcessor,
  IRetailerSetDrillDownKpi,
  IRetailerSetDrillDownFarmer,
  IRetailerDrillDownAssessmentParts,
  IRetailerKpiSetTopFarmers,
  IRetailerSetTopFarmersPending,
  IRetailerFarmerData,
  IRetailerFilterSpecies,
  IRetailerFilterProject,
  IRetailerFilterProcessor,
  IRetailerDrillDownAssessmentPartsClear,
} from './types';

const retailerKpiPendingAction = (
  isLoading: boolean,
): IRetailerKpiPendingInfo => ({
  type: 'RETAILER_KPI_PENDING_INFO',
  payload: isLoading,
});

const setRetailerKpiAction = (
  data: Array<RetailerKpiValueModel>,
): IRetailerSetKpisInfo => ({
  type: 'RETAILER_KPI_SET_KPI_INFO',
  payload: data,
});

const setRetailerKpiSelectedProjectAction = (
  projectName: string,
): IRetailerSetSelectedProject => ({
  type: 'RETAILER_KPI_SET_SELECTED_PROJECT',
  payload: projectName,
});

const setRetailerSelectedProcessorAction = (
  processor: string,
): IRetailerSetSelectedProcessor => ({
  type: 'RETAILER_KPI_SET_SELECTED_PROCESSOR',
  payload: processor,
});

const setRetailerSelectedFarmerAction = (
  farmer: string,
): IRetailerSetSelectedFarmer => ({
  type: 'RETAILER_KPI_SET_SELECTED_FARMER',
  payload: farmer,
});

const setRetailerSelectedSystemTypeAction = (
  systemType: string,
): IRetailerSetSelectedSystemType => ({
  type: 'RETAILER_KPI_SET_SELECTED_SYSTEM_TYPE',
  payload: systemType,
});

const setRetailerKpiProjectsInfo = (
  projects: Array<ProjectInformationModel>,
): IRetailerKpiSetProjectsInfo => ({
  type: 'RETAILER_KPI_SET_PROJECTS_INFO',
  payload: projects,
});

export const setRetailerDrillDownKpi = (
  data: Array<RetailerKpiValueModel>,
): IRetailerSetDrillDownKpi => ({
  type: 'RETAILER_DRILLDOWN_SET_KPI',
  payload: data,
});

export const setRetailerDrillDownFarmer = (
  farmer: string,
): IRetailerSetDrillDownFarmer => ({
  type: 'RETAILER_DRILLDOWN_SET_FARMER',
  payload: farmer,
});

export const setRetailerFilterSpecies = (
  species: any,
): IRetailerFilterSpecies => ({
  type: 'RETAILER_SET_FILTER_SPECIES',
  payload: species,
});

export const setRetailerFilterProject = (
  projects: any,
): IRetailerFilterProject => ({
  type: 'RETAILER_SET_FILTER_PROJECT',
  payload: projects,
});

export const setRetailerFilterProcessor = (
  processor: any,
): IRetailerFilterProcessor => ({
  type: 'RETAILER_SET_FILTER_PROCESSOR',
  payload: processor,
});

export const setRetailerDrillDownProcessor = (
  processor: string,
): IRetailerSetDrillDownProcessor => ({
  type: 'RETAILER_DRILLDOWN_SET_PROCESSOR',
  payload: processor,
});

export const setRetailerDrillDownProject = (
  project: string,
): IRetailerSetDrillDownProject => ({
  type: 'RETAILER_DRILLDOWN_SET_PROJECT',
  payload: project,
});

export const setRetailerKpiDrillDownSelectedParts = (
  id: number,
): IRetailerDrillDownAssessmentParts => ({
  type: 'RETAILER_DRILL_DOWN_SET_ASSESSMENT_PARTS',
  payload: id,
});

export const setRetailerKpiDrillDownSelectedPartsClear = (
): IRetailerDrillDownAssessmentPartsClear => ({
  type: 'RETAILER_DRILL_DOWN_SET_ASSESSMENT_PARTS_CLEAR',
});

export const setRetailerKpiFarmersPendingAction = (
  isLoading: boolean,
): IRetailerSetTopFarmersPending => ({
  type: 'RETAILER_SET_TOP_FARMERS_PENDING',
  payload: isLoading,
});

export const setRetailersKpiTopFarmersAction = (
  data: { data: Array<IRetailerFarmerData>, filters: any },
): IRetailerKpiSetTopFarmers => ({
  type: 'RETAILER_SET_TOP_FARMERS',
  payload: data,
});

const getRetailerKpi: ActionCreator<
  ThunkAction<Promise<any>, any, null, any>
> = (info) => async (dispatch: Dispatch) => {
  dispatch(retailerKpiPendingAction(true));
  let data = {
    "selectedSpecies": info?.selectedFarmers || [],
    "selectedProcessors": info?.selectedProcessors || [],
    "selectedProjects": info?.selectedProjects || [],
    "selectedSystemTypes": info?.selectedSystemTypes || [],
  }
  try {
    const [
      generalKpiResponse,
      drillldownKpiResponse,
      projectsInfo,
    ] = await Promise.all([
      Api(false).post('/api/KPI/GetKpiValues', {...data, DrillDown: false}),
      Api(false).post('/api/KPI/GetKpiValues', {...data, DrillDown: true}),
      Api(false).get('/api/KPI/GetProjectInformation'),
    ]);
    dispatch(setRetailerKpiAction(generalKpiResponse.data));
    dispatch(setRetailerDrillDownKpi(drillldownKpiResponse.data));
    dispatch(setRetailerKpiProjectsInfo(projectsInfo.data));
  } catch (e) {
    console.log(e);
  } finally {
    dispatch(retailerKpiPendingAction(false));
  }
};

const getRetailerKpiTopFarmers: ActionCreator<
  ThunkAction<Promise<any>, any, null, any>
> = (data: any) => async (dispatch: Dispatch) => {
  dispatch(setRetailerKpiFarmersPendingAction(true));
  try {
    const response = await Api(false).post(
      '/api/KPI/GetRetailerSupplierDrilldownTable',
      data,
    );
    dispatch(
      setRetailersKpiTopFarmersAction({ data: response.data, filters: data }),
    );
  } catch (e) {
    console.log(e);
  } finally {
    dispatch(setRetailerKpiFarmersPendingAction(false));
  }
};

export {
  getRetailerKpi,
  setRetailerKpiAction,
  setRetailerKpiSelectedProjectAction,
  setRetailerSelectedFarmerAction,
  setRetailerSelectedProcessorAction,
  setRetailerSelectedSystemTypeAction,
  getRetailerKpiTopFarmers,
};
